import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import type { AreaId } from '@/navigation/areas';
import { areaPickerItems, type AreaPicker, type PickerLocation } from '@/navigation/areaPicker';
import { isGateOpen } from '@/navigation/gates';
import { useAreaSavedViews } from '@/composables/useAreaSavedViews';

/**
 * What an area offers in the header, kept current as you move and as your
 * saved views load or change. The views come from the same query as the
 * sidebar's, so a view saved or renamed anywhere shows here at once. Until
 * they load, the area counts as having none.
 */
export function useAreaPicker(areaId: MaybeRefOrGetter<AreaId>, location: MaybeRefOrGetter<PickerLocation>) {
    const { views, isLoading, isError } = useAreaSavedViews(areaId);
    return computed<LiveAreaPicker>(() => ({
        ...areaPickerItems({ areaId: toValue(areaId), gateIsOpen: isGateOpen, views: views.value, location: toValue(location) }),
        viewsAreKnown: !isLoading.value && !isError.value,
    }));
}

export interface LiveAreaPicker extends AreaPicker {
    /**
     * True once every saved view has loaded. Until then, or if they failed to
     * load, a view missing from the picker may only be not here yet.
     */
    viewsAreKnown: boolean;
}
